import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const RecommendationDetails = ({ recommendation }) => {
  if (!recommendation) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <Icon name="Sparkles" size={48} color="var(--color-muted-foreground)" />
        <p className="text-sm text-muted-foreground mt-4">
          اختر توصية لعرض التفاصيل
        </p>
      </div>
    );
  }

  const data = recommendation?.recommendationData;
  const propertyMatches = data?.propertyMatches || [];
  const contractTiming = data?.contractTiming || {};
  const marketInsights = data?.marketInsights || {};

  const getUrgencyColor = (urgency) => {
    if (urgency?.includes('عاجل') || urgency?.includes('high')) return 'text-error';
    if (urgency?.includes('متوسط') || urgency?.includes('medium')) return 'text-warning';
    return 'text-success';
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <h3 className="text-lg md:text-xl font-heading font-bold text-foreground">
              توصيات ذكية للعميل
            </h3>
            <p className="text-xs md:text-sm text-muted-foreground mt-1">
              {recommendation?.clientName} • {recommendation?.clientCategory}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Sparkles" size={20} color="var(--color-primary)" />
            <span className="text-xs text-primary font-medium">مدعوم بالذكاء الاصطناعي</span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="space-y-6">
          {/* Summary */}
          {data?.summary && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                <p className="text-sm text-foreground">{data?.summary}</p>
              </div>
            </div>
          )}

          {/* Property Matches */}
          <div>
            <h4 className="text-sm md:text-base font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Home" size={18} />
              العقارات المطابقة ({propertyMatches?.length})
            </h4>
            <div className="space-y-4">
              {propertyMatches?.map((match, index) => (
                <div key={index} className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex items-center gap-1">
                          <Icon name="TrendingUp" size={16} color="var(--color-success)" />
                          <span className="text-lg font-bold text-success">{match?.matchScore}%</span>
                        </div>
                        <span className="text-xs text-muted-foreground">درجة التطابق</span>
                      </div>
                      {match?.property && (
                        <div className="flex items-center gap-3 mb-2">
                          {match?.property?.images?.[0] && (
                            <Image
                              src={match?.property?.images?.[0]}
                              alt={match?.property?.title || 'عقار'}
                              className="w-16 h-16 rounded-lg object-cover"
                            />
                          )}
                          <div>
                            <h5 className="text-sm font-semibold text-foreground">{match?.property?.title}</h5>
                            <p className="text-xs text-muted-foreground">
                              {match?.property?.city} • {match?.property?.type}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-foreground mb-3">{match?.reasoning}</p>
                  {match?.highlights && match?.highlights?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-success mb-2 flex items-center gap-1">
                        <Icon name="CheckCircle" size={14} />
                        المميزات:
                      </p>
                      <ul className="space-y-1">
                        {match?.highlights?.map((highlight, idx) => (
                          <li key={idx} className="text-xs text-foreground flex items-start gap-2">
                            <span className="text-success mt-0.5">•</span>
                            <span>{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {match?.concerns && match?.concerns?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-warning mb-2 flex items-center gap-1">
                        <Icon name="AlertCircle" size={14} />
                        نقاط للانتباه:
                      </p>
                      <ul className="space-y-1">
                        {match?.concerns?.map((concern, idx) => (
                          <li key={idx} className="text-xs text-foreground flex items-start gap-2">
                            <span className="text-warning mt-0.5">•</span>
                            <span>{concern}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Contract Timing */}
          <div>
            <h4 className="text-sm md:text-base font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Clock" size={18} />
              التوقيت الأمثل لمراجعة العقود
            </h4>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <Icon name="Calendar" size={16} color="var(--color-primary)" />
                  <span className="text-sm font-semibold text-foreground">
                    {contractTiming?.optimalReviewDate}
                  </span>
                </div>
                <span className={`text-xs font-medium ${getUrgencyColor(contractTiming?.urgencyLevel)}`}>
                  {contractTiming?.urgencyLevel}
                </span>
              </div>
              <p className="text-sm text-foreground mb-3">{contractTiming?.reasoning}</p>
              {contractTiming?.seasonalFactors && contractTiming?.seasonalFactors?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-2">العوامل الموسمية:</p>
                  <ul className="space-y-1">
                    {contractTiming?.seasonalFactors?.map((factor, idx) => (
                      <li key={idx} className="text-xs text-foreground flex items-start gap-2">
                        <Icon name="Leaf" size={12} className="mt-0.5" />
                        <span>{factor}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Market Insights */}
          <div>
            <h4 className="text-sm md:text-base font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="TrendingUp" size={18} />
              رؤى السوق والتحليل
            </h4>
            <div className="bg-card border border-border rounded-lg p-4 space-y-4">
              {marketInsights?.priceAnalysis && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                    <Icon name="DollarSign" size={14} />
                    تحليل الأسعار:
                  </p>
                  <p className="text-sm text-foreground">{marketInsights?.priceAnalysis}</p>
                </div>
              )}
              {marketInsights?.currentTrends && marketInsights?.currentTrends?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                    <Icon name="Activity" size={14} />
                    الاتجاهات الحالية:
                  </p>
                  <ul className="space-y-1">
                    {marketInsights?.currentTrends?.map((trend, idx) => (
                      <li key={idx} className="text-xs text-foreground flex items-start gap-2">
                        <span className="text-primary mt-0.5">•</span>
                        <span>{trend}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {marketInsights?.investmentOpportunities && marketInsights?.investmentOpportunities?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-success mb-2 flex items-center gap-1">
                    <Icon name="TrendingUp" size={14} />
                    فرص استثمارية:
                  </p>
                  <ul className="space-y-1">
                    {marketInsights?.investmentOpportunities?.map((opportunity, idx) => (
                      <li key={idx} className="text-xs text-foreground flex items-start gap-2">
                        <span className="text-success mt-0.5">•</span>
                        <span>{opportunity}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {marketInsights?.riskFactors && marketInsights?.riskFactors?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-error mb-2 flex items-center gap-1">
                    <Icon name="AlertTriangle" size={14} />
                    عوامل المخاطرة:
                  </p>
                  <ul className="space-y-1">
                    {marketInsights?.riskFactors?.map((risk, idx) => (
                      <li key={idx} className="text-xs text-foreground flex items-start gap-2">
                        <span className="text-error mt-0.5">•</span>
                        <span>{risk}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <p className="text-muted-foreground mb-1">تاريخ الإنشاء:</p>
                <p className="text-foreground font-medium">
                  {new Date(recommendation?.createdAt)?.toLocaleString('ar-EG')}
                </p>
              </div>
              {recommendation?.approvedAt && (
                <div>
                  <p className="text-muted-foreground mb-1">تاريخ الموافقة:</p>
                  <p className="text-foreground font-medium">
                    {new Date(recommendation?.approvedAt)?.toLocaleString('ar-EG')}
                  </p>
                </div>
              )}
              {recommendation?.sentAt && (
                <div>
                  <p className="text-muted-foreground mb-1">تاريخ الإرسال:</p>
                  <p className="text-foreground font-medium">
                    {new Date(recommendation?.sentAt)?.toLocaleString('ar-EG')}
                  </p>
                </div>
              )}
              {recommendation?.rejectionReason && (
                <div className="col-span-2">
                  <p className="text-muted-foreground mb-1">سبب الرفض:</p>
                  <p className="text-error font-medium">{recommendation?.rejectionReason}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecommendationDetails;